import './bootstrap.js';

if (typeof window !== 'undefined') {
    window.tailwind = window.tailwind || {};
    window.tailwind.config = {
        darkMode: 'class',
        theme: {
            extend: {
                colors: {
                    /* Must match resources/css/app.css :root --primary / --color-primary */
                    primary: '#c8392b',
                    'hu-green': '#436436',
                    'hu-green-dark': '#2d4524',
                    'pale-green': '#e8f0e4',
                    'background-light': '#f8f9fa',
                },
                fontFamily: {
                    sans: ['Public Sans', 'system-ui', 'sans-serif'],
                    display: ['Public Sans', 'sans-serif'],
                },
                borderRadius: {
                    DEFAULT: '0.25rem',
                    lg: '0.5rem',
                    xl: 'var(--radius-xl)',
                    '2xl': '1rem',
                    full: '9999px',
                },
            },
        },
    };
}
import { createApp, defineAsyncComponent, h } from 'vue';
import store from './store/index.js';
import SelectAppLanguage from './components/inputs/SelectAppLanguage.vue';
import SelectAppCurrency from './components/inputs/SelectAppCurrency.vue';
import GetProducts from './components/inputs/GetProducts.vue';
import VignettePrices from './components/VignettePrices.vue';
import VignettePricingTable from './components/VignettePricingTable.vue';
import VignetteCategoryPricingTable from './components/VignetteCategoryPricingTable.vue';
import ContactLanguages from './components/ContactLanguages.vue';
import PaymentMethodsPills from './components/PaymentMethodsPills.vue';
import PaymentMethodsCountrySelect from './components/PaymentMethodsCountrySelect.vue';
import PaymentMethodsGrid from './components/PaymentMethodsGrid.vue';
import MobileHeader from './components/MobileHeader.vue';
import HeaderLanguageSelector from './components/HeaderLanguageSelector.vue';
import HowItWorksValidityCheckForm from './components/HowItWorksValidityCheckForm.vue';
import HelpCenterLink from './components/HelpCenterLink.vue';
import LanguageSelectorBlock from './components/LanguageSelectorBlock.vue';
import WhyChooseUsLanguageDropdown from './components/WhyChooseUsLanguageDropdown.vue';
import Team4Flags from './components/Team4Flags.vue';
import { resolveHelpCenterUrl } from './shared/helpCenterResolver.js';
import { schedulePrefetchFormCatalog } from './shared/prefetchFormCatalog.js';
import { revealAfterSpaMount } from './shared/spa-reveal.js';
import { filterLicensePlateInput } from './shared/licensePlateInput.js';
import { initFaqDetailsGroups } from './app/pageScripts.js';

/** Heavy pages/widgets — async so their scoped CSS is not injected on every page (unused-CSS audit). */
const SelectVignette = defineAsyncComponent(() => import('./components/BuyVignette/steps/SelectVignette.vue'));
const EnterDetails = defineAsyncComponent(() => import('./components/BuyVignette/steps/EnterDetails.vue'));
const Payment = defineAsyncComponent(() => import('./components/BuyVignette/steps/Payment.vue'));
const BuyVignette = defineAsyncComponent(() => import('./components/BuyVignette/BuyVignette.vue'));
const ShoppingCart = defineAsyncComponent(() => import('./components/ShoppingCart.vue'));
const PricingSection = defineAsyncComponent(() => import('./components/PricingSection.vue'));
const HowItWorksPricingMock = defineAsyncComponent(() => import('./components/HowItWorksPricingMock.vue'));
const PaymentMethodsBuyCards = defineAsyncComponent(() => import('./components/PaymentMethodsBuyCards.vue'));
const HowItWorksSummaryTotal = defineAsyncComponent(() => import('./components/HowItWorksSummaryTotal.vue'));
const ValidityCheck = defineAsyncComponent(() => import('./components/ValidityCheck.vue'));
const HungaryTollMapSection = defineAsyncComponent(() => import('./components/HungaryTollMapSection.vue'));
const PaymentProcessing = defineAsyncComponent(() => import('./components/PaymentProcessing.vue'));

import router from './router.js';
import { startAttributionCaptureLifecycle, runAttributionCapture } from './attribution/capture.js';
import { setApiLanguageIdResolver } from './utils/apiLanguageId.js';

import vuetify, { resolveVuetifyLocale } from './plugins/vuetify.js';
/* After frameworks: beat duplicate helpers (e.g. `.rounded-xl { border-radius: 24px !important }`). */
import '../css/utilities-override.css';

const app = createApp({
    components: {
        SelectAppLanguage,
        SelectAppCurrency,
        GetProducts,
        VignettePrices,
        VignettePricingTable,
        VignetteCategoryPricingTable,
        SelectVignette,
        EnterDetails,
        Payment,
        BuyVignette,
        PaymentProcessing
    },
    data() {
        return {
            // Add your Vue data here
        }
    },
});

app.use(store);
app.use(vuetify);
app.use(router);

/** URL + GA cookies → `evh_attribution_v1` + plain keys (gclid, client_id, …) for webhooks / api_tags */
startAttributionCaptureLifecycle();
router.afterEach(() => {
    runAttributionCapture();
});
app.component('payment-processing', PaymentProcessing);
app.component('PricingSection', PricingSection);
app.component('HowItWorksPricingMock', HowItWorksPricingMock);
app.component('how-it-works-pricing-mock', HowItWorksPricingMock);
app.component('HowItWorksSummaryTotal', HowItWorksSummaryTotal);
app.component('how-it-works-summary-total', HowItWorksSummaryTotal);
app.component('ValidityCheck', ValidityCheck);
app.component('validity-check', ValidityCheck);
app.component('ContactLanguages', ContactLanguages);
app.component('contact-languages', ContactLanguages);
app.component('PaymentMethodsPills', PaymentMethodsPills);
app.component('payment-methods-pills', PaymentMethodsPills);
app.component('PaymentMethodsCountrySelect', PaymentMethodsCountrySelect);
app.component('payment-methods-country-select', PaymentMethodsCountrySelect);
app.component('PaymentMethodsGrid', PaymentMethodsGrid);
app.component('payment-methods-grid', PaymentMethodsGrid);
app.component('LanguageSelectorBlock', LanguageSelectorBlock);
app.component('language-selector-block', LanguageSelectorBlock);
app.component('WhyChooseUsLanguageDropdown', WhyChooseUsLanguageDropdown);
app.component('why-choose-us-language-dropdown', WhyChooseUsLanguageDropdown);
app.component('Team4Flags', Team4Flags);
app.component('team-4-flags', Team4Flags);
app.component('HelpCenterLink', HelpCenterLink);
app.component('help-center-link', HelpCenterLink);

window.switchCurrency = function (currencyCode) {
    const code = String(currencyCode || '').toUpperCase();
    if (!code) return;
    store.dispatch('currency/setCurrencyByCode', code);
};
window.getHelpCenterUrl = () => {
    const lang = window.APP_LANG ?? window.__APP_DATA__?.lang;
    return resolveHelpCenterUrl(lang);
};

/** Must run before app.mount: Vue runs child mounted() before parent, so loadInitialData would otherwise use stale localStorage.langId. */
const bootstrapAppData = typeof window !== 'undefined' ? window.__APP_DATA__ : null;
if (bootstrapAppData) {
    store.dispatch('initState', {
        project: bootstrapAppData.project,
        page: bootstrapAppData.page,
        texts: bootstrapAppData.texts,
        lang: bootstrapAppData.lang,
        languages: bootstrapAppData.languages,
        countries: bootstrapAppData.countries,
        currency: bootstrapAppData.currency,
        currencies: bootstrapAppData.currencies,
        defPath: bootstrapAppData.defPath,
        mobileView: bootstrapAppData.mobileView,
        isSecondHomePage: bootstrapAppData.isSecondHomePage,
    });
    const initialCurrencyCode =
        store.getters['currency/currentCurrency']?.code || bootstrapAppData.currency?.code;
    if (initialCurrencyCode) {
        window.switchCurrency(initialCurrencyCode);
    }

    store.dispatch('form/loadInitialData').catch(() => {});
}

vuetify.locale.current.value = resolveVuetifyLocale(
    bootstrapAppData?.lang || store.getters.lang
);
store.watch(
    () => store.getters.lang,
    (nextLang) => {
        vuetify.locale.current.value = resolveVuetifyLocale(nextLang);
    }
);
store.watch(
    () => store.getters.lang?.id,
    (newId, oldId) => {
        if (oldId == null || newId == null || newId === oldId) return;
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('langId', String(newId));
        }
        store.dispatch('form/reloadCountries').catch(() => {});
    }
);

setApiLanguageIdResolver(() => {
    const id = store.state.lang?.id;
    if (id !== undefined && id !== null && id !== '') return id;
    if (typeof localStorage !== 'undefined') {
        return localStorage.getItem('langId');
    }
    return null;
});

/** CMS Blade may inline <script> for mount props; Vue cannot compile those inside #app. */
function readMountProps(el, windowKey) {
    if (!el) return null;
    const raw = el.getAttribute('data-mount-props') || el.dataset.mountProps || '';
    if (raw) {
        try {
            return JSON.parse(raw);
        } catch (_) {}
    }
    if (typeof window !== 'undefined' && windowKey && window[windowKey] != null) {
        return window[windowKey];
    }
    return null;
}

function stripSideEffectTagsFromMountRoot(root) {
    if (!root) return;
    root.querySelectorAll('script, style').forEach((node) => node.remove());
}

function initHomeSection10Buy() {
    if (typeof window === 'undefined' || window.__evignetteHomeSection10BuyBound) return;
    window.__evignetteHomeSection10BuyBound = true;

    function mergePlateAndNavigate(form) {
        const cta = form.querySelector('a[data-home-section-10-cta]');
        const buyUrl =
            (cta && cta.getAttribute('href')) ||
            form.getAttribute('data-buy-url') ||
            form.getAttribute('action');
        const input = form.querySelector('[data-home-section-10-plate]');
        if (!buyUrl || !input) return;

        const plate = filterLicensePlateInput(String(input.value || '').trim());
        if (!plate) {
            input.value = '';
            input.focus();
            return;
        }
        input.value = plate;

        let merged = {};
        try {
            const raw = localStorage.getItem('vignetteFormData');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    merged = parsed;
                }
            }
        } catch (_) {}

        merged.license_plate = plate;

        try {
            localStorage.setItem('vignetteFormData', JSON.stringify(merged));
            localStorage.setItem('currentStep', 'select-vignette');
        } catch (_) {}

        window.location.href = buyUrl;
    }

    document.addEventListener(
        'input',
        (e) => {
            const input = e.target?.closest?.('[data-home-section-10-plate]');
            if (!input) return;
            const filtered = filterLicensePlateInput(input.value);
            if (filtered !== input.value) {
                input.value = filtered;
            }
        },
        true
    );

    document.addEventListener(
        'click',
        (e) => {
            const cta = e.target?.closest?.('a[data-home-section-10-cta]');
            if (!cta) return;
            const form = cta.closest('[data-home-section-10-buy]');
            if (!form) return;
            e.preventDefault();
            mergePlateAndNavigate(form);
        },
        true
    );

    document.addEventListener(
        'keydown',
        (e) => {
            if (e.key !== 'Enter') return;
            const input = e.target?.closest?.('[data-home-section-10-plate]');
            if (!input) return;
            const form = input.closest('[data-home-section-10-buy]');
            if (!form) return;
            e.preventDefault();
            mergePlateAndNavigate(form);
        },
        true
    );
}

const appRoot = document.getElementById('app');
/**
 * Compiling the whole CMS document is a long main-thread task. Do it only when
 * the page actually contains Vue components. The homepage only needs the
 * currency control, which is mounted on its own.
 */
const fullDocumentSelector = [
    'select-app-language',
    'get-products',
    'vignette-prices',
    'vignette-pricing-table',
    'vignette-category-pricing-table',
    'select-vignette',
    'enter-details',
    'payment',
    'buy-vignette',
    'payment-processing',
    'how-it-works-pricing-mock',
    'how-it-works-summary-total',
    'validity-check',
    'contact-languages',
    'payment-methods-pills',
    'payment-methods-country-select',
    'payment-methods-grid',
    'language-selector-block',
    'why-choose-us-language-dropdown',
    'team-4-flags',
    'help-center-link',
].join(',');

if (appRoot) {
    stripSideEffectTagsFromMountRoot(appRoot);
    if (appRoot.querySelector(fullDocumentSelector)) {
        app.mount(appRoot);
    } else {
        appRoot.querySelectorAll('select-app-currency').forEach((el) => {
            const currencyApp = createApp(SelectAppCurrency);
            currencyApp.use(store);
            currencyApp.mount(el);
        });
    }
} else {
    console.warn('[evignettehungary] #app not found — Vue root not mounted');
}

initHomeSection10Buy();
initFaqDetailsGroups();

schedulePrefetchFormCatalog(store);

/**
 * Home can render multiple pricing blocks (e.g. hero + section 4). Duplicate id="pricing-app" is invalid HTML;
 * getElementById would attach only to the first node and wrong `data-section-objects` (missing product keys).
 * Mount one Vue app per matching root so each block uses its own CMS objects JSON.
 * Idle-deferred so below-fold scoped CSS is not in the initial unused-CSS inventory.
 */
mountWhenIdle(() => {
    const pricingRoots =
        typeof document !== 'undefined' ? Array.from(document.querySelectorAll('#pricing-app')) : [];
    for (const pricingEl of pricingRoots) {
        const pricingVariant = pricingEl.dataset.variant || null;
        const standardPriceFromBlade = pricingEl.dataset.standardPrice || '';
        let sectionObjects = {};
        let sectionTexts = {};
        let sectionContents = {};
        try {
            const raw =
                pricingEl.getAttribute('data-section-objects') || pricingEl.dataset.sectionObjects || '';
            if (raw) sectionObjects = JSON.parse(raw);
        } catch (_) {}
        if (
            typeof window !== 'undefined'
            && sectionObjects
            && typeof sectionObjects === 'object'
            && !Array.isArray(sectionObjects)
            && Object.keys(sectionObjects).length
        ) {
            window.__HOME_SECTION_4_PRICING_OBJECTS__ = {
                ...(window.__HOME_SECTION_4_PRICING_OBJECTS__ || {}),
                ...sectionObjects,
            };
        }
        try {
            const raw =
                pricingEl.getAttribute('data-section-texts') || pricingEl.dataset.sectionTexts || '';
            if (raw) sectionTexts = JSON.parse(raw);
        } catch (_) {}
        try {
            const raw =
                pricingEl.getAttribute('data-section-contents') || pricingEl.dataset.sectionContents || '';
            if (raw) sectionContents = JSON.parse(raw);
        } catch (_) {}
        const pricingApp = createApp({
            template:
                '<PricingSection :variant="variant" :section-objects="sectionObjects" :section-texts="sectionTexts" :section-contents="sectionContents" :standard-price-from-blade="standardPriceFromBlade" />',
            data: () => ({
                variant: pricingVariant,
                sectionObjects,
                sectionTexts,
                sectionContents,
                standardPriceFromBlade
            })
        });
        pricingApp.use(store);
        pricingApp.component('PricingSection', PricingSection);
        pricingApp.mount(pricingEl);
    }

    const homeTollMapEl = document.getElementById('home-toll-map-app');
    const homeTollMapProps = readMountProps(homeTollMapEl, '__HOME_TOLL_MAP__');
    if (homeTollMapEl && homeTollMapProps && typeof homeTollMapProps === 'object') {
        mountRoot(HungaryTollMapSection, homeTollMapEl, homeTollMapProps);
    }

    const pmBuyEl = document.getElementById('pm-buy-cards-app');
    if (pmBuyEl) {
        let sectionObjects = {};
        try {
            const raw =
                pmBuyEl.getAttribute('data-section-objects')
                || pmBuyEl.dataset.sectionObjects
                || '';
            if (raw) sectionObjects = JSON.parse(raw);
        } catch (_) {}
        if (
            typeof window !== 'undefined'
            && sectionObjects
            && typeof sectionObjects === 'object'
            && !Array.isArray(sectionObjects)
            && Object.keys(sectionObjects).length
        ) {
            window.__PAYMENT_METHODS_CMS_OBJECTS__ = {
                ...(window.__PAYMENT_METHODS_CMS_OBJECTS__ || {}),
                ...sectionObjects,
            };
        }
        const fallbackUrl =
            pmBuyEl.getAttribute('data-fallback-url')
            || pmBuyEl.dataset.fallbackUrl
            || '/buy-vignette';
        const buyLabel =
            pmBuyEl.getAttribute('data-buy-label')
            || pmBuyEl.dataset.buyLabel
            || '';
        mountRoot(
            PaymentMethodsBuyCards,
            pmBuyEl,
            { sectionObjects, fallbackUrl, buyLabel },
            [store],
        );
    }
});

const mobileHeaderEl = document.getElementById('mobile-header-app');
const mobileHeaderProps = readMountProps(mobileHeaderEl, '__MOBILE_HEADER_DATA__');
if (mobileHeaderEl && mobileHeaderProps) {
    const mobileHeaderApp = createApp(MobileHeader, mobileHeaderProps);
    mobileHeaderApp.use(store);
    mobileHeaderApp.mount('#mobile-header-app');
}

function resolveHeaderLanguageProps() {
    if (typeof window === 'undefined') return null;
    const pushed = window.__HEADER_LANG_DATA__;
    if (pushed && typeof pushed === 'object') return pushed;
    const appData = window.__APP_DATA__;
    if (!appData || typeof appData !== 'object') return null;
    return {
        currentLang: appData.lang ?? null,
        languages: Array.isArray(appData.languages) ? appData.languages : [],
        pathsByLanguage: appData.page?.paths_by_language ?? {},
    };
}

/** Desktop header nav uses `<details>`; keep dropdown behavior without relying on a Blade @stack. */
function initHeaderNavDropdowns() {
    const header = document.querySelector('header');
    if (!header) return;
    const nav = header.querySelector('nav');
    if (!nav) return;

    function closeAllDropdowns() {
        nav.querySelectorAll('details').forEach((d) => {
            d.removeAttribute('open');
        });
    }

    document.addEventListener(
        'click',
        (e) => {
            const summary = e.target.closest('summary');
            const insideNav = e.target.closest('header nav');

            if (summary && summary.closest('nav') === nav) {
                e.preventDefault();
                e.stopPropagation();
                const clickedDetails = summary.closest('details');
                const wasOpen = clickedDetails?.hasAttribute('open');
                closeAllDropdowns();
                if (!wasOpen && clickedDetails) clickedDetails.setAttribute('open', '');
                return;
            }

            if (!insideNav) {
                closeAllDropdowns();
            }
        },
        true,
    );
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initHeaderNavDropdowns);
    } else {
        initHeaderNavDropdowns();
    }
}

const headerLangEl = document.getElementById('header-language-selector-app');
const headerLangProps = resolveHeaderLanguageProps();
if (headerLangEl && headerLangProps) {
    const headerLangApp = createApp(HeaderLanguageSelector, headerLangProps);
    headerLangApp.mount('#header-language-selector-app');
}

const helpCenterLinkEl = document.getElementById('help-center-link');
if (helpCenterLinkEl) {
    const helpCenterLinkApp = createApp({});
    helpCenterLinkApp.use(store);
    helpCenterLinkApp.component('HelpCenterLink', HelpCenterLink);
    helpCenterLinkApp.component('help-center-link', HelpCenterLink);
    helpCenterLinkApp.mount('#help-center-link');
}

function mountWhenIdle(fn) {
    if (typeof window === 'undefined') return;
    const run = () => {
        try {
            fn();
        } catch (e) {
            console.error(e);
        }
    };
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 2500 });
    } else {
        window.addEventListener('load', () => setTimeout(run, 1), { once: true });
    }
}

/**
 * Async components cannot be the root of createApp(): when the chunk resolves,
 * Vue calls insertBefore on a parent that is already null and throws.
 * A sync wrapper keeps that parent mounted.
 */
function mountRoot(component, el, props = {}, plugins = []) {
    const app = createApp({
        name: 'MountRoot',
        render() {
            return h(component, props);
        },
    });
    plugins.forEach((plugin) => app.use(plugin));
    app.mount(el);
    return app;
}

function mountShoppingCartMini(mountId, dataKey) {
    const el = document.getElementById(mountId);
    const props = readMountProps(el, dataKey);
    if (!el || !props) return;
    mountRoot(ShoppingCart, el, props, [store, vuetify]);
}
/* Hero carts: mount ASAP (above the fold). Below-fold widgets use mountWhenIdle. */
[
    'desktop-hero-cart-app',
    'mobile-hero-cart-app',
    'howtobuy-desktop-hero-cart-app',
    'howtobuy-hero-cart-app',
].forEach((id) => mountShoppingCartMini(id));

const howItWorksValidityEl = document.getElementById('how-it-works-validity-check-app');
if (howItWorksValidityEl) {
    const props = readMountProps(howItWorksValidityEl) || {};
    mountRoot(HowItWorksValidityCheckForm, howItWorksValidityEl, props, [store]);
}

revealAfterSpaMount();

/* Editorial Policy & general content page behavior */
const TERMS_OF_USE_ANCHOR_OFFSET_PX = 90;

function scrollToInPageTarget(target, smooth) {
    if (!target) return;
    const termRoot = document.querySelector('.term-of-use-anchor-offset');
    if (termRoot && termRoot.contains(target)) {
        const y = target.getBoundingClientRect().top + window.scrollY - TERMS_OF_USE_ANCHOR_OFFSET_PX;
        window.scrollTo({ top: Math.max(0, y), behavior: smooth ? 'smooth' : 'auto' });
        return;
    }
    target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
}

function scrollTermsOfUseHashFromLocation(smooth) {
    const hash = typeof location !== 'undefined' ? location.hash : '';
    if (!hash || hash === '#') return;
    let target;
    try {
        target = document.querySelector(hash);
    } catch (err) {
        return;
    }
    scrollToInPageTarget(target, smooth);
}

document.addEventListener('DOMContentLoaded', function () {
    requestAnimationFrame(function () {
        scrollTermsOfUseHashFromLocation(false);
    });

    window.addEventListener('hashchange', function () {
        scrollTermsOfUseHashFromLocation(true);
    });

    document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
        anchor.addEventListener('click', function (e) {
            const href = this.getAttribute('href');
            if (href === '#') return;
            e.preventDefault();
            const target = document.querySelector(href);
            scrollToInPageTarget(target, true);
        });
    });
    document.querySelectorAll('form[data-no-submit]').forEach(function (form) {
        form.addEventListener('submit', function (e) { e.preventDefault(); });
    });
});

window.UIUtils = window.UIUtils || {};
window.UIUtils.toggleElement = function (selector) {
    const el = document.querySelector(selector);
    if (el) el.style.display = el.style.display === 'none' ? '' : 'none';
};

// Article detail helpfulness widget
window.handleFeedback = function (type) {
    const yesBtn = document.getElementById('btn-yes');
    const noBtn = document.getElementById('btn-no');
    const thanks = document.getElementById('feedback-thanks');

    if (!yesBtn || !noBtn || !thanks) return;

    yesBtn.classList.remove('border-teal-400', 'bg-teal-50', 'text-teal-700');
    noBtn.classList.remove('border-red-300', 'bg-red-50', 'text-red-600');

    if (type === 'yes') {
        yesBtn.classList.add('border-teal-400', 'bg-teal-50', 'text-teal-700');
    } else {
        noBtn.classList.add('border-red-300', 'bg-red-50', 'text-red-600');
    }

    thanks.classList.remove('hidden');
};

// Submit request page interactions
window.updateCharCount = function (inputId, countId, max) {
    const input = document.getElementById(inputId);
    const counter = document.getElementById(countId);
    if (!input || !counter) return;

    const len = input.value.length;
    counter.textContent = `${len} / ${max}`;
    if (len > max * 0.9) {
        counter.classList.add('text-amber-500');
        counter.classList.remove('text-gray-400');
    } else {
        counter.classList.remove('text-amber-500');
        counter.classList.add('text-gray-400');
    }
};

window.handleDragOver = function (e) {
    e.preventDefault();
    const zone = document.getElementById('upload-zone');
    if (!zone) return;
    zone.style.borderColor = '#1e3a8a';
    zone.style.backgroundColor = '#eff6ff';
};

window.handleDragLeave = function () {
    const zone = document.getElementById('upload-zone');
    if (!zone) return;
    zone.style.borderColor = '#d1d5db';
    zone.style.backgroundColor = '';
};

window.renderFileList = function (files) {
    const list = document.getElementById('file-list');
    if (!list) return;

    list.innerHTML = '';
    if (!files || files.length === 0) {
        list.classList.add('hidden');
        return;
    }

    list.classList.remove('hidden');
    Array.from(files).forEach(function (file) {
        const parts = String(file.name || '').split('.');
        const ext = (parts.length > 1 ? parts.pop() : '').toUpperCase();
        const size = `${(file.size / 1024).toFixed(0)} KB`;
        const item = document.createElement('div');
        item.className = 'flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-small px-4 py-2.5';
        item.innerHTML =
            '<span class="material-symbols-outlined text-blue-900 text-base flex-shrink-0">attach_file</span>' +
            `<span class="text-sm font-medium text-gray-800 flex-1 truncate">${file.name}</span>` +
            `<span class="text-xs text-gray-400 flex-shrink-0">${size}</span>` +
            `<span class="text-xs font-bold text-blue-900 bg-blue-50 border border-blue-100 rounded-small px-2 py-0.5 flex-shrink-0">${ext}</span>`;
        list.appendChild(item);
    });
};

window.handleDrop = function (e) {
    e.preventDefault();
    window.handleDragLeave();
    const files = e?.dataTransfer?.files;
    window.renderFileList(files);
};

window.handleFileSelect = function (e) {
    window.renderFileList(e?.target?.files);
};

document.addEventListener('DOMContentLoaded', function () {
    const consent = document.getElementById('consent');
    if (!consent) return;

    consent.addEventListener('change', function () {
        const icon = document.getElementById('check-icon');
        if (!icon) return;
        if (this.checked) icon.classList.remove('hidden');
        else icon.classList.add('hidden');
    });
});
